/* ─────────────────────────────────────────────────────────────────────────────
 * Structured Literacy 4-Week WRITING Curriculum
 *
 * Mirrors the reading curriculum scope & sequence. Each writing lesson uses
 * multi-sensory techniques: phoneme-grapheme mapping, dictation, and structured
 * composition. Follows the structured-literacy principle: reading and spelling reinforce each other.
 *
 * IDs: OGW{week}-{nn}  (e.g. OGW1-01, OGW4-02)
 * ─────────────────────────────────────────────────────────────────────────── */

import type { WritingLessonDetail } from './offlineWritingLessons';

export type { WritingLessonDetail };

// ─── Week 1: Short Vowel Spelling ────────────────────────────────────────────

const ogWritingWeek1: WritingLessonDetail[] = [
  {
    id: 'OGW1-01',
    title: 'Spell Short Vowel CVC Words',
    titleFr: 'Épelle les mots CVC avec voyelle courte',
    level: 1,
    contentBlocks: [
      {
        type: 'text',
        content: 'Structured Literacy Writing Lesson 1 — Short Vowel Spelling\n\nToday you will SPELL words using the short vowels you know.\n\nRemember the sounds:\n• short /a/ → "cat"\n• short /e/ → "bed"\n• short /i/ → "pin"\n• short /o/ → "hot"\n• short /u/ → "cup"\n\n🖐 STRATEGY: Tap out each sound with your finger, then write each letter.',
        contentFr: 'Leçon d\'écriture structurée 1 — Orthographe des voyelles courtes\n\nAujourd\'hui tu vas ÉPELER des mots avec les voyelles courtes.\n\n🖐 STRATÉGIE : Tape chaque son avec ton doigt, puis écris chaque lettre.',
      },
      {
        type: 'spelling',
        content: 'Tap & Spell: a pet you might have 🐱 (3 sounds: /k/-/ă/-/t/)',
        contentFr: 'Tape & Épelle: un animal domestique 🐱 (3 sons : /ch/-/a/-/t/)',
        answer: 'cat',
        answerFr: 'chat',
      },
      {
        type: 'spelling',
        content: 'Tap & Spell: where you sleep 🛏 (3 sounds: /b/-/ĕ/-/d/)',
        contentFr: 'Tape & Épelle : où tu dors 🛏 (3 sons : /l/-/ĭ/-/t/)',
        answer: 'bed',
        answerFr: 'lit',
      },
      {
        type: 'spelling',
        content: 'Tap & Spell: the opposite of cold 🔥 (3 sounds: /h/-/ŏ/-/t/)',
        contentFr: 'Tape & Épelle : le contraire de froid 🔥 (3 sons : /ch/-/o/-/d/)',
        answer: 'hot',
        answerFr: 'chaud',
      },
      {
        type: 'spelling',
        content: 'Tap & Spell: you drink from this ☕ (3 sounds: /k/-/ŭ/-/p/)',
        contentFr: 'Tape & Épelle : on boit dedans ☕ (3 sons : /t/-/a/-/s/-/s/)',
        answer: 'cup',
        answerFr: 'tasse',
      },
      {
        type: 'spelling',
        content: 'Tap & Spell: you write with this ✏️ (3 sounds: /p/-/ĕ/-/n/)',
        contentFr: 'Tape & Épelle : on écrit avec ✏️ (3 sons : /s/-/t/-/y/-/l/-/o/)',
        answer: 'pen',
        answerFr: 'stylo',
      },
      {
        type: 'spelling',
        content: 'Tap & Spell: Earth goes around this ☀️ (3 sounds: /s/-/ŭ/-/n/)',
        contentFr: 'Tape & Épelle : la Terre tourne autour de ☀️ (6 sons)',
        answer: 'sun',
        answerFr: 'soleil',
      },
      {
        type: 'text',
        content: '✅ You spelled 6 short vowel words! Remember: tap the sounds first, then write them. This is how structured-literacy learners spell — sound by sound! 🌟',
        contentFr: '✅ Tu as épelé 6 mots avec des voyelles courtes ! Rappelle-toi : tape les sons d\'abord, puis écris-les. 🌟',
      },
    ],
  },

  {
    id: 'OGW1-02',
    title: 'Dictation Sentences — Short Vowels',
    titleFr: 'Phrases dictées — Voyelles courtes',
    level: 1,
    contentBlocks: [
      {
        type: 'text',
        content: 'Structured Literacy Writing Lesson 2 — Short Vowel Dictation\n\nDICTATION is when you listen to a sentence and write it from memory. This builds both spelling AND reading at the same time!\n\n📋 STEPS:\n1. Read the sentence below\n2. Say it aloud\n3. Cover it with your hand\n4. Write it from memory\n5. Uncover and check!',
        contentFr: 'Leçon d\'écriture structurée 2 — Dictée de voyelles courtes\n\nLa DICTÉE, c\'est quand tu écoutes une phrase et tu l\'écris de mémoire.\n\n📋 ÉTAPES :\n1. Lis la phrase\n2. Dis-la à voix haute\n3. Cache-la\n4. Écris-la de mémoire\n5. Découvre et vérifie !',
      },
      {
        type: 'free-write',
        content: 'Dictation 1: "The fat cat sat on the mat."\n(Cover and write from memory)',
        contentFr: 'Dictée 1 : "Le gros chat était assis sur le tapis."\n(Cache et écris de mémoire)',
      },
      {
        type: 'free-write',
        content: 'Dictation 2: "The pup ran in the mud and got wet."\n(Cover and write from memory)',
        contentFr: 'Dictée 2 : "Le chiot courut dans la boue et fut mouillé."\n(Cache et écris de mémoire)',
      },
      {
        type: 'free-write',
        content: 'Dictation 3: "Dan and Pam can hop, skip, and jump."\n(Cover and write from memory)',
        contentFr: 'Dictée 3 : "Dan et Pam peuvent sauter, gambader et bondir."\n(Cache et écris de mémoire)',
      },
      {
        type: 'text',
        content: '🔍 CHECK YOUR WORK:\n• Did every word have a short vowel?\n• Did you tap the sounds as you wrote?\n• Did each sentence start with a capital letter?\n• Did each sentence end with a period?\n\nCorrect any mistakes and rewrite the sentence. Practice makes perfect! ✏️',
        contentFr: '🔍 VÉRIFIE TON TRAVAIL :\n• Chaque mot avait-il une voyelle courte ?\n• As-tu tapé les sons en écrivant ?\n• Chaque phrase commence-t-elle par une majuscule ?\n• Chaque phrase se termine-t-elle par un point ?\n\nCorrige les erreurs et réécris la phrase. C\'est en pratiquant qu\'on progresse ! ✏️',
      },
    ],
  },
];

// ─── Week 2: Blends & Digraphs Spelling ──────────────────────────────────────

const ogWritingWeek2: WritingLessonDetail[] = [
  {
    id: 'OGW2-01',
    title: 'Spelling Words with Blends',
    titleFr: 'Épeler des mots avec des groupes de consonnes',
    level: 2,
    contentBlocks: [
      {
        type: 'text',
        content: 'Structured Literacy Writing Lesson 3 — Spelling Blends\n\nWhen you SPELL a blend, write BOTH consonant letters even though they sound fast and close together!\n\n🖐 TAP first, then spell:\n• "flag" → /f/-/l/-/ă/-/g/ → f-l-a-g\n• "trip" → /t/-/r/-/ĭ/-/p/ → t-r-i-p\n• "best" → /b/-/ĕ/-/s/-/t/ → b-e-s-t',
        contentFr: 'Leçon d\'écriture structurée 3 — Épeler des groupes\n\nQuand tu ÉPELLES un groupe, écris LES DEUX lettres consonnes !\n\n🖐 TAPE d\'abord, puis épelle.',
      },
      {
        type: 'spelling',
        content: 'Spell: it waves on a pole 🚩 (blend at start: /f/-/l/-/ă/-/g/)',
        contentFr: 'Épelle: il flotte en haut d\'un mât 🚩',
        answer: 'flag',
        answerFr: 'drapeau',
      },
      {
        type: 'spelling',
        content: 'Spell: what your hands do when you\'re happy 👏 (blend: /k/-/l/-/ă/-/p/)',
        contentFr: 'Épelle: ce que font tes mains quand tu es content 👏',
        answer: 'clap',
        answerFr: 'clap',
      },
      {
        type: 'spelling',
        content: 'Spell: water falling drop by drop 💧 (blend: /d/-/r/-/ĭ/-/p/)',
        contentFr: 'Épelle: de l\'eau qui tombe goutte à goutte 💧',
        answer: 'drip',
        answerFr: 'goutte',
      },
      {
        type: 'spelling',
        content: 'Spell: a seafloor creature with claws 🦀 (blend: /k/-/r/-/ă/-/b/)',
        contentFr: 'Épelle: une créature marine avec des pinces 🦀',
        answer: 'crab',
        answerFr: 'crabe',
      },
      {
        type: 'spelling',
        content: 'Spell: to say you are the best 😄 (blend: /b/-/r/-/ă/-/g/)',
        contentFr: 'Épelle: dire que tu es le meilleur 😄',
        answer: 'brag',
        answerFr: 'vanter',
      },
      {
        type: 'fill-blank',
        content: 'The sled went do_n the hill fast! (hint: the ow team)',
        contentFr: 'La luge descendit la colline vite ! (indice : groupe final)',
        answer: 'w',
        answerFr: 'w',
      },
      {
        type: 'text',
        content: '💡 BLEND SPELLING TIP for dyslexia readers:\nWhen you hear a blend, slow down and tap EACH letter.\n"flag" → say /f/… /l/… /a/… /g/ — write one letter per sound.\nNever skip the L in "flag" or the R in "frog"! They are both there! 🐸',
        contentFr: '💡 CONSEIL pour épeler les groupes :\nQuand tu entends un groupe, ralentis et tape CHAQUE lettre.\n"drapeau" → dis /d/… /r/… /a/… /p/… — écris une lettre par son.\nNe saute jamais le R dans "drapeau" ! 🐸',
      },
    ],
  },

  {
    id: 'OGW2-02',
    title: 'Spelling Digraphs — sh, ch, th, wh',
    titleFr: 'Épeler les digrammes — ch, ou, th, wh',
    level: 2,
    contentBlocks: [
      {
        type: 'text',
        content: 'Structured Literacy Writing Lesson 4 — Spelling Digraphs\n\nDigraphs are TWO letters that make ONE sound. You MUST write both letters!\n\n• /sh/ sound → write "sh" (ship, wish, fish)\n• /ch/ sound → write "ch" (chip, much, bench)\n• /th/ sound → write "th" (thin, with, math)\n• /wh/ sound → write "wh" (when, which, where)\n• /f/ sound from "ph" → write "ph" (photo, graph)',
        contentFr: 'Leçon d\'écriture structurée 4 — Épeler les digrammes\n\nLes digrammes sont DEUX lettres qui font UN son. Tu DOIS écrire les deux lettres !\n\n• son /ch/ → écris "ch" (chat, bouche)\n• son /ou/ → écris "ou" (loupe, tour)',
      },
      {
        type: 'spelling',
        content: 'Spell: where a boat docks ⚓ (digraph: sh + ore)',
        contentFr: 'Épelle: là où un bateau accoste ⚓',
        answer: 'shore',
        answerFr: 'rive',
      },
      {
        type: 'spelling',
        content: 'Spell: a small piece of potato 🍟 (digraph: ch + ip)',
        contentFr: 'Épelle: un petit morceau de pomme de terre 🍟',
        answer: 'chip',
        answerFr: 'chip',
      },
      {
        type: 'spelling',
        content: 'Spell: not thick — the opposite 📏 (digraph: th + in)',
        contentFr: 'Épelle: pas épais — le contraire 📏',
        answer: 'thin',
        answerFr: 'mince',
      },
      {
        type: 'spelling',
        content: 'Spell: a question word about time ❓ (digraph: wh + en)',
        contentFr: 'Épelle: un mot de question sur le temps ❓',
        answer: 'when',
        answerFr: 'quand',
      },
      {
        type: 'spelling',
        content: 'Spell: numbers and equations are this subject 📐 (digraph: m + ath)',
        contentFr: 'Épelle: les chiffres et les équations — cette matière 📐',
        answer: 'math',
        answerFr: 'maths',
      },
      {
        type: 'free-write',
        content: 'Write a sentence using TWO digraph words. Try: "The fish wished to shop for chips." Or make your own!',
        contentFr: 'Écris une phrase avec DEUX mots à digramme. Essaie : "Le poisson voulait acheter des chips." Ou invente la tienne !',
      },
      {
        type: 'text',
        content: '🌟 Digraph spelling trick: When you hear /sh/, /ch/, or /th/ — always write TWO letters. Your ear hears one sound but your hand writes two! Great work! 🎊',
        contentFr: '🌟 Astuce digramme : Quand tu entends /ch/ ou /ou/ — écris toujours DEUX lettres. Ton oreille entend un son mais ta main en écrit deux ! Bravo ! 🎊',
      },
    ],
  },
];

// ─── Week 3: Long Vowel Spelling ──────────────────────────────────────────────

const ogWritingWeek3: WritingLessonDetail[] = [
  {
    id: 'OGW3-01',
    title: 'Spelling Magic E Words (VCe Pattern)',
    titleFr: 'Épeler les mots à E magique (modèle VCe)',
    level: 3,
    contentBlocks: [
      {
        type: 'text',
        content: 'Structured Literacy Writing Lesson 5 — Spelling with Magic E\n\nWhen the vowel says its LONG name inside a word ending in "e" — you write the VCe pattern:\nV = vowel, C = consonant, e = silent e\n\n• long /ā/ → a_e: cake, name, tape\n• long /ī/ → i_e: bike, time, pine\n• long /ō/ → o_e: home, note, rope\n• long /ū/ → u_e: cube, tube, cute\n• long /ē/ → e_e: theme, these\n\n🖐 TAP & SPELL: only 3 taps even though there are 4 letters! (The e is silent)',
        contentFr: 'Leçon d\'écriture structurée 5 — Épeler avec le E magique\n\nQuand la voyelle dit son NOM LONG dans un mot qui finit en "e" — tu écris le modèle VCe.\n\n🖐 TAPE & ÉPELLE : seulement 3 taps même s\'il y a 4 lettres ! (Le e est muet)',
      },
      {
        type: 'spelling',
        content: 'Tap (3 sounds) & Spell: you bake this for birthdays 🎂 (a_e pattern)',
        contentFr: 'Tape (3 sons) & Épelle: on fait ça pour les anniversaires 🎂 (modèle â_e)',
        answer: 'cake',
        answerFr: 'gâteau',
      },
      {
        type: 'spelling',
        content: 'Tap (3 sounds) & Spell: a flying toy on a string 🪁 (i_e pattern)',
        contentFr: 'Tape (3 sons) & Épelle: un jouet volant attaché à un fil 🪁',
        answer: 'kite',
        answerFr: 'cerf-volant',
      },
      {
        type: 'spelling',
        content: 'Tap (3 sounds) & Spell: where your family lives 🏠 (o_e pattern)',
        contentFr: 'Tape (3 sons) & Épelle: là où ta famille vit 🏠',
        answer: 'home',
        answerFr: 'maison',
      },
      {
        type: 'spelling',
        content: 'Tap (3 sounds) & Spell: a 3D square shape 🎲 (u_e pattern)',
        contentFr: 'Tape (3 sons) & Épelle: une forme cubique 🎲',
        answer: 'cube',
        answerFr: 'cube',
      },
      {
        type: 'fill-blank',
        content: 'Add the silent e to make a new word: hop → hop_ (long vowel!)',
        contentFr: 'Ajoute le e muet pour faire un nouveau mot : sot → sot_',
        answer: 'e',
        answerFr: 'e',
      },
      {
        type: 'fill-blank',
        content: 'Add the silent e: pin → pin_ (the /ī/ says its name!)',
        contentFr: 'Ajoute le e muet : fin → fin_',
        answer: 'e',
        answerFr: 'e',
      },
      {
        type: 'text',
        content: '🔄 TRANSFORM these short vowel words to long vowel words by adding a silent e:\n• cap → ___e  (cape)\n• bit → ___e  (bite)\n• not → ___e  (note)\n• cut → ___e  (cute)\n• us  → ___e  (use)\n\nThe silent e is like a magic wand — it changes the whole word! 🪄',
        contentFr: '🔄 TRANSFORME ces mots à voyelle courte en mots à voyelle longue en ajoutant un e muet :\n• mat → ___e (mate)\n• fin → ___e (fine)\n\nLe e muet est comme une baguette magique ! 🪄',
      },
    ],
  },

  {
    id: 'OGW3-02',
    title: 'Spelling Vowel Teams — Which One to Use?',
    titleFr: 'Épeler les équipes de voyelles — Laquelle choisir ?',
    level: 3,
    contentBlocks: [
      {
        type: 'text',
        content: 'Structured Literacy Writing Lesson 6 — Spelling Vowel Teams\n\nVowel teams make one long sound. The tricky part: sometimes two teams make the SAME sound!\n\nLong /ā/:\n• "ai" usually in the middle: rain, mail, tail\n• "ay" usually at the end: day, play, stay\n\nLong /ō/:\n• "oa" usually in the middle: boat, road, coat\n• "ow" usually at the end: snow, grow, flow\n\nLong /ē/:\n• "ee" in the middle or end: tree, feet, see\n• "ea" in the middle: eat, leaf, dream\n\nTip: "When two vowels go walking, the first one does the talking!" 🚶‍♂️🚶',
        contentFr: 'Leçon d\'écriture structurée 6 — Épeler les équipes de voyelles\n\nLes équipes de voyelles font un son long. La partie difficile : parfois deux équipes font le MÊME son !\n\nLong /ā/ : "ai" au milieu (pluie, main), "ay" à la fin (hay, play)\nLong /ō/ : "oa" au milieu (bateau), "ow" à la fin (neige)\nLong /ē/ : "ee" ou "ea" au milieu',
      },
      {
        type: 'spelling',
        content: 'Spell: precipitation that falls from clouds 🌧️ (ai team: r-ai-n)',
        contentFr: 'Épelle: les précipitations qui tombent des nuages 🌧️',
        answer: 'rain',
        answerFr: 'pluie',
      },
      {
        type: 'spelling',
        content: 'Spell: you ride this on water ⛵ (oa team: b-oa-t)',
        contentFr: 'Épelle: tu roules dessus sur l\'eau ⛵',
        answer: 'boat',
        answerFr: 'bateau',
      },
      {
        type: 'spelling',
        content: 'Spell: frozen water from the sky ❄️ (ow team: sn-ow)',
        contentFr: 'Épelle: l\'eau gelée qui tombe du ciel ❄️',
        answer: 'snow',
        answerFr: 'neige',
      },
      {
        type: 'spelling',
        content: 'Spell: a large plant with branches 🌳 (ee team: tr-ee)',
        contentFr: 'Épelle: une grande plante avec des branches 🌳',
        answer: 'tree',
        answerFr: 'arbre',
      },
      {
        type: 'spelling',
        content: 'Spell: you do this with food 🍎 (ea team: e-at)',
        contentFr: 'Épelle: tu fais ça avec de la nourriture 🍎',
        answer: 'eat',
        answerFr: 'manger',
      },
      {
        type: 'free-write',
        content: 'Write a sentence using at least TWO vowel team words. Ideas: rain, boat, snow, tree, eat, play, day. Try: "On a rainy day, I eat and read by the tree."',
        contentFr: 'Écris une phrase avec au moins DEUX mots à équipe de voyelles. Idées : pluie, bateau, neige, arbre, manger.',
      },
      {
        type: 'text',
        content: '🌟 You are a vowel team speller! Remember:\n• "ai" and "ay" both say /ā/\n• "oa" and "ow" both say /ō/\n• "ee" and "ea" both say /ē/\nPractice will help you remember which to use! ⭐',
        contentFr: '🌟 Tu es un expert des équipes de voyelles ! ⭐',
      },
    ],
  },
];

// ─── Week 4: Multi-Syllabic & Structured Composition ─────────────────────────

const ogWritingWeek4: WritingLessonDetail[] = [
  {
    id: 'OGW4-01',
    title: 'Syllable Division Spelling (2-Syllable Words)',
    titleFr: 'Épeler par division de syllabes (2 syllabes)',
    level: 3,
    contentBlocks: [
      {
        type: 'text',
        content: 'Structured Literacy Writing Lesson 7 — Spelling 2-Syllable Words\n\nTo SPELL a 2-syllable word, SPLIT it first!\n\nStep 1: Say the word aloud: "basket"\nStep 2: Clap the syllables: "bas" - "ket" (2 claps)\nStep 3: Spell the first syllable: b-a-s\nStep 4: Spell the second syllable: k-e-t\nStep 5: Put them together: "basket"\n\n🖐 Tap each syllable as you spell it!',
        contentFr: 'Leçon d\'écriture structurée 7 — Épeler des mots à 2 syllabes\n\nPour ÉPELER un mot à 2 syllabes, DIVISE-LE d\'abord !\n\nÉtape 1 : Dis le mot à voix haute : "lapin"\nÉtape 2 : Frappe les syllabes : "la" - "pin" (2 frappes)\nÉtape 3 : Épelle la première syllabe : l-a\nÉtape 4 : Épelle la deuxième syllabe : p-i-n\nÉtape 5 : Combine : "lapin"',
      },
      {
        type: 'spelling',
        content: 'Clap & Spell: a furry animal with long ears 🐇 (2 claps: rab-bit)',
        contentFr: 'Frappe & Épelle: un animal à longues oreilles 🐇 (2 frappes : la-pin)',
        answer: 'rabbit',
        answerFr: 'lapin',
      },
      {
        type: 'spelling',
        content: 'Clap & Spell: you grow vegetables here 🥕 (2 claps: gar-den)',
        contentFr: 'Frappe & Épelle: on y fait pousser des légumes 🥕 (2 frappes : jar-din)',
        answer: 'garden',
        answerFr: 'jardin',
      },
      {
        type: 'spelling',
        content: 'Clap & Spell: you rest your head on this at night 😴 (2 claps: pil-low)',
        contentFr: 'Frappe & Épelle: tu poses ta tête dessus la nuit 😴 (2 frappes : o-reiller)',
        answer: 'pillow',
        answerFr: 'oreiller',
      },
      {
        type: 'spelling',
        content: 'Clap & Spell: outdoor lunch in nature 🧺 (2 claps: pic-nic)',
        contentFr: 'Frappe & Épelle: déjeuner dehors dans la nature 🧺 (2 frappes : pique-nique)',
        answer: 'picnic',
        answerFr: 'pique-nique',
      },
      {
        type: 'sentence-build',
        content: 'rabbit / garden / the / in / lives / The',
        contentFr: 'lapin / jardin / dans / le / vit / Le',
        answer: 'The rabbit lives in the garden',
        answerFr: 'Le lapin vit dans le jardin',
      },
      {
        type: 'text',
        content: '🌟 2-syllable spelling rule:\nFor "closed + closed" words (like rab-bit, pic-nic):\n• Double consonant in the middle → split between them!\n• rab|bit (not ra|bbit)\n• pic|nic (not pi|cnic)\n\nYou\'re building your word-knowledge syllable by syllable! 🧩',
        contentFr: '🌟 Règle pour les mots à 2 syllabes fermées :\n• Consonne double au milieu → divise entre les deux !\n• rab|bit, pic|nic\n\nTu construis ton vocabulaire syllabe par syllabe ! 🧩',
      },
    ],
  },

  {
    id: 'OGW4-02',
    title: '3-Syllable Word Spelling & Dictation',
    titleFr: 'Épeler les mots à 3 syllabes et dictée',
    level: 4,
    contentBlocks: [
      {
        type: 'text',
        content: 'Structured Literacy Writing Lesson 8 — Spelling 3-Syllable Words\n\n3-syllable words look big, but they\'re just 3 small chunks!\n\nExamples with syllable split:\n• but|ter|fly → b-u-t + t-e-r + f-l-y\n• el|e|phant → e-l + e + ph-a-n-t\n• um|brel|la → u-m + b-r-e-l + l-a\n• fan|tas|tic → f-a-n + t-a-s + t-i-c\n• hap|pi|ness → h-a-p + p-i + n-e-s-s\n\n🎵 SING IT: Say each syllable like a rhythm — it helps memory!',
        contentFr: 'Leçon d\'écriture structurée 8 — Épeler les mots à 3 syllabes\n\nLes mots à 3 syllabes semblent grands, mais ce ne sont que 3 petits morceaux !\n\n• pa|pil|lon\n• é|lé|phant\n• pa|ra|pluie\n• fan|tas|tique\n• bon|heur',
      },
      {
        type: 'spelling',
        content: 'Clap 3x & Spell: a large grey animal with a trunk 🐘 (el-e-phant)',
        contentFr: 'Frappe 3x & Épelle: un grand animal gris avec une trompe 🐘',
        answer: 'elephant',
        answerFr: 'éléphant',
      },
      {
        type: 'spelling',
        content: 'Clap 3x & Spell: an insect with colorful wings 🦋 (but-ter-fly)',
        contentFr: 'Frappe 3x & Épelle: un insecte avec de belles ailes colorées 🦋',
        answer: 'butterfly',
        answerFr: 'papillon',
      },
      {
        type: 'spelling',
        content: 'Clap 3x & Spell: a device you type on 💻 (com-put-er)',
        contentFr: 'Frappe 3x & Épelle: un appareil sur lequel tu tapes 💻',
        answer: 'computer',
        answerFr: 'ordinateur',
      },
      {
        type: 'spelling',
        content: 'Clap 3x & Spell: something really amazing and great 🌟 (fan-tas-tic)',
        contentFr: 'Frappe 3x & Épelle: quelque chose de vraiment incroyable 🌟',
        answer: 'fantastic',
        answerFr: 'fantastique',
      },
      {
        type: 'free-write',
        content: 'Dictation — Read, cover, write from memory:\n"A butterfly landed on the computer screen. It was fantastic!"\n(Remember: 3-syllable words — clap each syllable as you write!)',
        contentFr: 'Dictée — Lis, cache, écris de mémoire :\n"Un papillon s\'est posé sur l\'écran de l\'ordinateur. C\'était fantastique !"\n(Rappel : mots à 3 syllabes — frappe chaque syllabe en écrivant !)',
      },
      {
        type: 'text',
        content: '💪 3-syllable spelling strategy:\n1. Say the word: "elephant"\n2. Tap syllables on fingers: el - e - phant (3 fingers)\n3. Spell each finger\'s syllable: el / e / phant\n4. Write it all together: elephant\n\nYou\'re a 3-syllable speller now! 🐘🦋',
        contentFr: '💪 Stratégie pour 3 syllabes :\n1. Dis le mot : "éléphant"\n2. Tape les syllabes sur les doigts : é - lé - phant (3 doigts)\n3. Épelle chaque syllabe : é / lé / phant\n4. Écris tout ensemble : éléphant 🐘',
      },
    ],
  },

  {
    id: 'OGW4-03',
    title: 'Structured Composition — Build a Story',
    titleFr: 'Composition structurée — Construis une histoire',
    level: 5,
    contentBlocks: [
      {
        type: 'text',
        content: 'Structured Literacy Writing Lesson 9 — Structured Story Composition\n\nNow we put ALL our skills together to WRITE a story!\n\nStrong writers use a PLAN before they write:\n\n📝 STORY PLAN:\n1. Who? (Character)\n2. Where? (Setting)\n3. What happened? (Problem/Event)\n4. What did they do? (Action)\n5. How did it end? (Resolution)\n\nChoose your character and setting from below, or make your own!',
        contentFr: 'Leçon d\'écriture structurée 9 — Composition d\'histoire structurée\n\nMaintenant on met TOUTES nos compétences ensemble pour ÉCRIRE une histoire !\n\nLes bons écrivains font un PLAN avant d\'écrire :\n\n📝 PLAN D\'HISTOIRE :\n1. Qui ? (Personnage)\n2. Où ? (Cadre)\n3. Que s\'est-il passé ? (Problème/Événement)\n4. Qu\'ont-ils fait ? (Action)\n5. Comment ça s\'est terminé ? (Résolution)',
      },
      {
        type: 'prompt',
        content: 'Step 1 — Plan your story.\nCharacter ideas: a brave rabbit, a kind elephant, a curious child, a blue butterfly\nSetting ideas: a garden, a lake, a school, a forest\nProblem ideas: lost something, wanted to help a friend, found something amazing\n\nWrite your plan (a few words for each step):',
        contentFr: 'Étape 1 — Planifie ton histoire.\nIdées de personnage : un lapin courageux, un éléphant gentil, un enfant curieux\nIdées de cadre : un jardin, un lac, une école, une forêt\nIdées de problème : a perdu quelque chose, voulait aider un ami\n\nÉcris ton plan :',
      },
      {
        type: 'free-write',
        content: 'Step 2 — Write your OPENING sentence.\nStart with your character and setting:\n"One day, [character] was in [place] when..."\n\nRemember: Start with a capital letter!',
        contentFr: 'Étape 2 — Écris ta phrase d\'OUVERTURE.\nCommence avec ton personnage et le cadre :\n"Un jour, [personnage] était dans [endroit] quand..."\n\nRappel : Commence par une majuscule !',
      },
      {
        type: 'free-write',
        content: 'Step 3 — Write WHAT HAPPENED (2-3 sentences).\nDescribe the event or problem.\nTry to include at least ONE multi-syllable word (butterfly, elephant, garden, fantastic, adventure...)!',
        contentFr: 'Étape 3 — Écris CE QUI S\'EST PASSÉ (2-3 phrases).\nDécris l\'événement ou le problème.\nEssaie d\'inclure au moins UN mot à plusieurs syllabes !',
      },
      {
        type: 'free-write',
        content: 'Step 4 — Write your ENDING (1-2 sentences).\nHow was the problem solved? How did your character feel?\nEnd with a period. Great endings often circle back to the beginning!',
        contentFr: 'Étape 4 — Écris ta FIN (1-2 phrases).\nComment le problème a-t-il été résolu ? Comment ton personnage s\'est-il senti ?\nTermine par un point.',
      },
      {
        type: 'text',
        content: '🔍 REVISE YOUR STORY — check these:\n☐ Every sentence starts with a capital letter\n☐ Every sentence ends with . or ! or ?\n☐ Short vowel words are spelled correctly\n☐ Long vowel words have the right pattern (magic e or vowel team)\n☐ Multi-syllable words are split and spelled syllable by syllable\n☐ Your story has a beginning, middle, and end\n\nYou are a REAL author! 📖✍️',
        contentFr: '🔍 RÉVISE TON HISTOIRE — vérifie :\n☐ Chaque phrase commence par une majuscule\n☐ Chaque phrase se termine par . ou ! ou ?\n☐ Les mots à voyelle courte sont bien orthographiés\n☐ Les mots à voyelle longue ont le bon modèle\n☐ Les mots à plusieurs syllabes sont divisés correctement\n\nTu es un VRAI auteur ! 📖✍️',
      },
    ],
  },

  {
    id: 'OGW4-04',
    title: 'Month-End Spelling Challenge — All Patterns',
    titleFr: 'Défi d\'orthographe de fin de mois — Tous les modèles',
    level: 4,
    contentBlocks: [
      {
        type: 'text',
        content: 'Structured Literacy Writing Lesson 10 — Month-End Mastery Challenge\n\nYou have learned EVERYTHING a structured-literacy writer needs:\n✅ Short vowel spelling (CVC)\n✅ Consonant blend spelling\n✅ Digraph spelling (sh, ch, th)\n✅ Magic E / VCe pattern\n✅ Vowel team spelling\n✅ 2-syllable word spelling\n✅ 3-syllable word spelling\n✅ Structured story composition\n\nNow PROVE IT with this final challenge! 🏆',
        contentFr: 'Leçon d\'écriture structurée 10 — Défi de maîtrise de fin de mois\n\nTu as appris TOUT ce dont un écrivain a besoin :\n✅ Orthographe des voyelles courtes (CVC)\n✅ Orthographe des groupes de consonnes\n✅ Orthographe des digrammes (ch, ou)\n✅ Modèle E magique / VCe\n✅ Orthographe des équipes de voyelles\n✅ Orthographe des mots à 2 syllabes\n✅ Orthographe des mots à 3 syllabes\n✅ Composition d\'histoire structurée\n\nMaintenent PROUVE-LE avec ce défi final ! 🏆',
      },
      {
        type: 'spelling',
        content: 'FINAL CHALLENGE 1 — Short vowel: where water collects 💧 (pond)',
        contentFr: 'DÉFI FINAL 1 — Voyelle courte : là où l\'eau se rassemble 💧',
        answer: 'pond',
        answerFr: 'étang',
      },
      {
        type: 'spelling',
        content: 'FINAL CHALLENGE 2 — Blend: what you do on a swing 🎠 (swing → sw + ing)',
        contentFr: 'DÉFI FINAL 2 — Groupe : ce qu\'on fait sur une balançoire 🎠',
        answer: 'swing',
        answerFr: 'balancer',
      },
      {
        type: 'spelling',
        content: 'FINAL CHALLENGE 3 — Digraph + Magic E: protect from the hot sun 🌞 (shade)',
        contentFr: 'DÉFI FINAL 3 — Digramme + E magique : protège du soleil brûlant 🌞',
        answer: 'shade',
        answerFr: 'ombre',
      },
      {
        type: 'spelling',
        content: 'FINAL CHALLENGE 4 — Long vowel vowel team: water from clouds 🌧️ (rain)',
        contentFr: 'DÉFI FINAL 4 — Équipe de voyelles longues : eau des nuages 🌧️',
        answer: 'rain',
        answerFr: 'pluie',
      },
      {
        type: 'spelling',
        content: 'FINAL CHALLENGE 5 — 2-syllable word: where you cook outdoors 🔥 (camp-fire)',
        contentFr: 'DÉFI FINAL 5 — 2 syllabes : où on cuisine en plein air 🔥',
        answer: 'campfire',
        answerFr: 'feu de camp',
      },
      {
        type: 'spelling',
        content: 'FINAL CHALLENGE 6 — 3-syllable word: an amazing voyage 🚀 (ad-ven-ture)',
        contentFr: 'DÉFI FINAL 6 — 3 syllabes : un voyage incroyable 🚀',
        answer: 'adventure',
        answerFr: 'aventure',
      },
      {
        type: 'free-write',
        content: 'FINAL CHALLENGE 7 — Dictation from memory:\n"The brave rabbit had a fantastic adventure. It swam in the rain, climbed a campfire hill, and found a beautiful home by the lake."\n\nRead it twice, cover it, then write the whole passage!',
        contentFr: 'DÉFI FINAL 7 — Dictée de mémoire :\n"Le lapin courageux eut une aventure fantastique. Il nagea sous la pluie, gravit une colline et trouva une belle maison près du lac."\n\nLis-le deux fois, cache-le, puis écris le passage entier !',
      },
      {
        type: 'text',
        content: '🏆 CONGRATULATIONS — 4-WEEK WRITING JOURNEY COMPLETE!\n\nYou have proven you can:\n🌟 Spell words from every vowel pattern\n🌟 Write sentences with correct mechanics\n🌟 Spell 2 and 3-syllable words by chunking\n🌟 Write a structured story with a beginning, middle, and end\n\nYou are a Writing Champion! 🎊📚',
        contentFr: '🏆 FÉLICITATIONS — PARCOURS D\'ÉCRITURE DE 4 SEMAINES TERMINÉ !\n\nTu as prouvé que tu peux :\n🌟 Épeler des mots de chaque modèle de voyelle\n🌟 Écrire des phrases avec la bonne mécanique\n🌟 Épeler des mots à 2 et 3 syllabes en les découpant\n🌟 Écrire une histoire structurée\n\nTu es un Champion Écrivain ! 🎊📚',
      },
    ],
  },
];

// ─── Assembled Structured Literacy Writing Curriculum ─────────────────────────────────────────

export const OG_WRITING_LESSONS: WritingLessonDetail[] = [
  ...ogWritingWeek1,
  ...ogWritingWeek2,
  ...ogWritingWeek3,
  ...ogWritingWeek4,
];

export const OG_WRITING_LESSON_IDS = OG_WRITING_LESSONS.map(l => l.id);

/** Get structured-literacy writing lessons for a given level */
export function getOGWritingLessonsForLevel(level: number): WritingLessonDetail[] {
  return OG_WRITING_LESSONS.filter(l => l.level <= level);
}

/** Get a single structured-literacy writing lesson by ID */
export function getOGWritingLessonById(id: string): WritingLessonDetail | undefined {
  return OG_WRITING_LESSONS.find(l => l.id === id);
}

/** Structured-literacy writing weeks grouped by ID */
export const OG_WRITING_WEEKS = {
  week1: ogWritingWeek1.map(l => l.id),  // Short Vowel Spelling
  week2: ogWritingWeek2.map(l => l.id),  // Blends & Digraphs
  week3: ogWritingWeek3.map(l => l.id),  // Long Vowel Spelling
  week4: ogWritingWeek4.map(l => l.id),  // Multi-Syllabic & Composition
} as const;
